<template>
  <!-- <Breadcrumbs :title="pageTitle" :modal="'createNew'" /> -->
  <div class="container-fluid">
    <div class="row starter-main mt-5 pt-5">
       <TableTitle
        :title="title"
        :pageTitle="pageTitle"
        icon="fas fa-book"
        :showAction="true"
        actionLabel="Create New"
        :modal="'createNew'"
        :disabled="!canCreate('data')"
      /> 
      <CommonTable2
        v-if="isDataLoaded"
        :tableObj="tableConfig"
        :title="title"
        @edit="handleEdit"
        @delete="handleDelete"
        @view="handleView"
        permissionKey="data"
      />
    </div>
  </div>

  <div
    v-if="isDataLoaded"
    class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4 flex-wrap"
  >
    <div class="d-flex align-items-center gap-2">
      <span class="text-muted">Rows per page:</span>
      <select
        class="form-select form-select-sm rows-select"
        v-model.number="tablePagination.per_page"
        @change="changeRows"
      >
        <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
          {{ n }}
        </option>
      </select>
    </div>

    <div class="pagination-mini">
      <button
        class="page-btn prev_btn"
        :disabled="!tablePagination.has_prev"
        @click="handlePageChange(tablePagination.current_page - 1)"
      >
        «
      </button>

      <button
        v-for="(p, index) in visiblePages"
        :key="index"
        class="page-btn"
        :class="{
          'page-active': p === tablePagination.current_page,
          'disabled-dots': p === '...',
        }"
        :disabled="p === '...'"
        @click="typeof p === 'number' && handlePageChange(p)"
      >
        {{ p }}
      </button>

      <button
        class="page-btn next_btn"
        :disabled="!tablePagination.has_next"
        @click="handlePageChange(tablePagination.current_page + 1)"
      >
        »
      </button>
    </div>
  </div>

  <div
    class="modal fade"
    id="createNew"
    :class="{ 'show': isCreateModalOpen }"
    tabindex="-1"
    aria-labelledby="createModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">Create {{ pageTitle }}</h3>
          <button
            class="btn-close py-0"
            type="button"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label" for="createTitle">Title *</label>
            <input
              type="text"
              class="form-control"
              id="createTitle"
              placeholder="Enter title"
              v-model="newCardStatus.title"
              required
            />
          </div>
          <div class="mb-3">
            <label class="form-label" for="createColor">Color</label>
            <input
              type="color"
              class="form-control form-control-color"
              id="createColor"
              v-model="newCardStatus.color"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" type="button" data-bs-dismiss="modal" @click="closeCreateModal">
            Close
          </button>
          <button class="btn btn-primary" type="button" @click="saveData" :disabled="!canCreate('data')">
            Save changes
          </button>
        </div>
      </div>
    </div>
  </div>

  <div
    class="modal fade"
    id="editModal"
    tabindex="-1"
    aria-labelledby="editModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">
            {{ isViewMode ? "View" : "Edit" }} {{ pageTitle }}
          </h3>
          <button
            class="btn-close py-0"
            type="button"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="closeEditModal"
          ></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label" for="editTitle">Title *</label>
            <input
              type="text"
              class="form-control"
              id="editTitle"
              placeholder="Enter title"
              v-model="editingCardStatus.title"
              :readonly="isViewMode"
              required
            />
          </div>
          <div class="mb-3">
            <label class="form-label" for="editColor">Color</label>
            <input
              v-if="!isViewMode"
              type="color"
              class="form-control form-control-color"
              id="editColor"
              v-model="editingCardStatus.color"
            />
            <div v-else>
              <span
                class="badge"
                :style="{ backgroundColor: editingCardStatus.color }"
              >
                {{ editingCardStatus.color }}
              </span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            class="btn btn-secondary"
            type="button"
            data-bs-dismiss="modal" @click="closeEditModal"
          >
            Close
          </button>
          <button
            v-if="!isViewMode"
            class="btn btn-primary"
            type="button"
            @click="updateData"
            :disabled="!canUpdate('data')"
          >
            Save changes
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import apiService from "@/services/apiService";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import TableTitle from "@/components/commen/table/TableTitle.vue";
import { usePermissions } from "@/composables/usePermissions";
const { canCreate, canUpdate } = usePermissions();

const API_RESOURCE = "/common/cardstatus";
const pageTitle = "Card Status";
const title = ref("Card Statuses List");

const isCreateModalOpen = ref(false);
const isViewMode = ref(false);
const isDataLoaded = ref(false);

const tableConfig = ref({
  columns: [
    { name: "Title", field: "title", sortable: true },
    { name: "Color", field: "color", sortable: true },
  ],
  data: [] as any[],
  actions: [true],
  loading: false,
  showCategoryFilter: false,
  showStatusFilter: false,
  table_width: "max-content",
});

const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 1,
  has_next: false,
  has_prev: false,
});

const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  if (!total || total <= 4) {
    return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  pages.push(1);

  if (current > 4) pages.push("...");

  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  if (current < total - 3) pages.push("...");

  pages.push(total);
  return pages;
});

const newCardStatus = ref({
  title: "",
  color: "#cccccc",
});

const editingCardStatus = ref({
  _id: "",
  title: "",
  color: "#cccccc",
  createdAt: "",
  updatedAt: "",
});

function success_with_icon(message: string) {
  toast.success(message, {
    hideProgressBar: true,
    autoClose: 2000,
    theme: "colored",
  });
}

function error_with_icon(message: string) {
  toast.error(message, {
    hideProgressBar: true,
    autoClose: 2000,
    theme: "colored",
  });
}

interface FetchOptions {
  page?: number;
  limit?: number;
}

const normalizeApiResponse = (
  payload: any
): { rows: any[]; pagination?: Record<string, any> } => {
  if (Array.isArray(payload)) {
    return { rows: payload };
  }

  const potentialArrays = [
    payload?.data,
    payload?.rows,
    payload?.items,
    payload?.result,
    payload?.cardStatuses,
    payload?.card_statuses,
    payload?.records,
    payload?.data?.rows,
    payload?.data?.items,
    payload?.data?.result,
    payload?.data?.cardStatuses,
    payload?.data?.card_statuses,
  ];

  for (const candidate of potentialArrays) {
    if (Array.isArray(candidate)) {
      return {
        rows: candidate,
        pagination: payload?.pagination || payload?.data?.pagination,
      };
    }
  }

  return { rows: [], pagination: payload?.pagination };
};

const syncPaginationState = (
  paginationMeta: Record<string, any> | undefined,
  fallback: { page: number; limit: number; totalItems: number }
) => {
  const resolvedPerPage =
    paginationMeta?.per_page ??
    paginationMeta?.perPage ??
    paginationMeta?.limit ??
    fallback.limit ??
    tablePagination.value.per_page;

  const fallbackTotal = fallback.totalItems;
  const resolvedTotal =
    paginationMeta?.total ??
    paginationMeta?.totalItems ??
    paginationMeta?.total_records ??
    fallbackTotal;

  const resolvedCurrent =
    paginationMeta?.current_page ??
    paginationMeta?.currentPage ??
    paginationMeta?.page ??
    fallback.page ??
    1;

  const computedTotalPages =
    paginationMeta?.total_pages ??
    paginationMeta?.totalPages ??
    (resolvedPerPage
      ? Math.ceil((resolvedTotal || 0) / resolvedPerPage)
      : tablePagination.value.total_pages);

  tablePagination.value.current_page = resolvedCurrent;
  tablePagination.value.per_page = resolvedPerPage || tablePagination.value.per_page;
  tablePagination.value.total = resolvedTotal ?? tableConfig.value.data.length;
  tablePagination.value.total_pages = Math.max(1, computedTotalPages || 1);
  tablePagination.value.has_prev = tablePagination.value.current_page > 1;
  tablePagination.value.has_next =
    tablePagination.value.current_page < tablePagination.value.total_pages;
};

const fetchData = async (
  { page = tablePagination.value.current_page, limit = tablePagination.value.per_page }: FetchOptions = {}
) => {
  tableConfig.value.loading = true;
  try {
    const response = await apiService.get(API_RESOURCE, { page, limit });
    const { rows, pagination } = normalizeApiResponse(response);
    tableConfig.value.data = Array.isArray(rows) ? [...rows] : [];
    syncPaginationState(pagination, {
      page,
      limit,
      totalItems: Array.isArray(rows) ? rows.length : 0,
    });
    isDataLoaded.value = true;

    const maxPage = Math.max(1, tablePagination.value.total_pages);
    if (page > maxPage) {
      await fetchData({ page: maxPage, limit });
      return;
    }
  } catch (error: any) {
    console.error("Error fetching Card Statuses:", error);
    tableConfig.value.data = [];
    error_with_icon(
      error.response?.data?.message ||
        error.message ||
        "Failed to load Card Status data."
    );
  } finally {
    tableConfig.value.loading = false;
    isDataLoaded.value = true;
  }
};

const saveData = async () => {
  if (!newCardStatus.value.title.trim()) {
    error_with_icon("Title is required.");
    return;
  }
  try {
    await apiService.post(API_RESOURCE, newCardStatus.value);
    success_with_icon("Card Status added successfully");
    closeCreateModal();
    newCardStatus.value = { title: "", color: "#cccccc" };
    await fetchData({ page: 1, limit: tablePagination.value.per_page });
  } catch (error: any) {
    console.error("Error saving Card Status:", error);
    error_with_icon(error.response?.data?.message || "Failed to add Card Status.");
  }
};

const updateData = async () => {
  if (!editingCardStatus.value.title.trim()) {
    error_with_icon("Title is required.");
    return;
  }
  try {
    const id = editingCardStatus.value._id;
    const updatePayload = {
      title: editingCardStatus.value.title,
      color: editingCardStatus.value.color,
    };
    await apiService.put(API_RESOURCE, id, updatePayload);
    success_with_icon("Card Status updated successfully");
    closeEditModal();
    await fetchData({
      page: tablePagination.value.current_page,
      limit: tablePagination.value.per_page,
    });
  } catch (error: any) {
    console.error("Error updating Card Status:", error);
    error_with_icon(error.response?.data?.message || "Failed to update Card Status.");
  }
};

const handleDelete = async (id: string) => {
  if (!confirm("Are you sure you want to delete this Card Status?")) {
    return;
  }
  try {
    await apiService.delete(API_RESOURCE, id);
    success_with_icon("Card Status deleted successfully");
    await fetchData({
      page: tablePagination.value.current_page,
      limit: tablePagination.value.per_page,
    });
  } catch (error: any) {
    console.error("Error deleting Card Status:", error);
    error_with_icon(error.response?.data?.message || "Failed to delete Card Status.");
  }
};

function showModal(modalId: string) {
  const modalElement = document.getElementById(modalId);
  if (modalElement) {
    const bootstrap = (window as any).bootstrap;
    if (bootstrap && bootstrap.Modal) {
      try {
        const modalInstance = bootstrap.Modal.getInstance(modalElement) || new bootstrap.Modal(modalElement);
        modalInstance?.show();
      } catch (e) {
        console.error(`Failed to show modal ${modalId} via Bootstrap JS:`, e);
        fallbackShow(modalElement);
      }
    } else {
      fallbackShow(modalElement);
    }
  }
}

function fallbackShow(modalElement: HTMLElement) {
  modalElement.classList.add('show');
  modalElement.style.display = 'block';
  document.body.classList.add('modal-open');
  if (!document.querySelector('.modal-backdrop')) {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop fade show';
    document.body.appendChild(backdrop);
  }
}

function hideModal(modalId: string) {
  const modalElement = document.getElementById(modalId);
  if (!modalElement) return;

  const bootstrap = (window as any).bootstrap;
  if (bootstrap && bootstrap.Modal) {
    try {
      const modalInstance = bootstrap.Modal.getInstance(modalElement);
      if (modalInstance) {
        modalInstance.hide();
        return; 
      }
    } catch (e) {
      console.warn(`Error hiding modal ${modalId} via Bootstrap JS, falling back to manual:`, e);
    }
  }
  fallbackHide(modalElement);
}

function fallbackHide(modalElement: HTMLElement) {
  modalElement.classList.remove('show');
  modalElement.style.display = 'none';
  const openModals = document.querySelectorAll('.modal.show');
  if (openModals.length === 0) {
    document.body.classList.remove('modal-open');
  }
  if (openModals.length === 0) {
     const backdrops = document.querySelectorAll('.modal-backdrop'); 
     backdrops.forEach(backdrop => backdrop.remove()); 
  }

}

const handleEdit = (row: any) => {
  isViewMode.value = false;
  editingCardStatus.value = { ...row };
  showModal('editModal');
};

const handleView = (row: any) => {
  isViewMode.value = true;
  editingCardStatus.value = { ...row };
  showModal('editModal');
};

const closeCreateModal = () => {
  hideModal("createNew");
  isCreateModalOpen.value = false;
  newCardStatus.value = { title: "", color: "#cccccc" };
};

const closeEditModal = () => {
  hideModal("editModal");
  isViewMode.value = false;
};

const openCreateModal = () => {
  isCreateModalOpen.value = true;
  showModal('createNew');
};

const changeRows = async () => {
  tablePagination.value.current_page = 1;
  await fetchData({ page: 1, limit: tablePagination.value.per_page });
};

const handlePageChange = async (page: number) => {
  if (typeof page !== "number" || Number.isNaN(page)) return;
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (
    tablePagination.value.total_pages &&
    page > tablePagination.value.total_pages
  ) {
    return;
  }
  await fetchData({ page, limit: tablePagination.value.per_page });
};

onMounted(async () => {
  await fetchData({
    page: tablePagination.value.current_page,
    limit: tablePagination.value.per_page,
  });
});
</script>

<style scoped>
.badge {
  font-size: 0.875em;
}

.rows-select {
  width: 82px;
  padding: 8px 17px;
  font-size: 0.85rem;
  background-color: transparent;
}

.pagination-mini {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
}

.page-btn {
  border: none;
  background: transparent;
  font-size: 1rem;
  padding: 7px 12px;
  cursor: pointer;
  color: #6c757d;
  border-radius: 0;
  transition: 0.2s;
  border: 1px solid #dee2e6;
  outline: none;
}

.page-btn:first-child {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}

.page-btn:last-child {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}

.page-btn:hover {
  background: #e9ecef;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-active {
  background: #21c1d6;
  color: #fff;
  padding: 7px 12px;
  font-weight: 600;
  min-width: 28px;
  text-align: center;
  display: inline-block;
  border: 1px solid #21c1d6;
}

.page-active:hover {
  background: #21c1d6;
}

.prev_btn,
.next_btn {
  color: #21c1d6;
}
</style>